import React, { useState, useEffect } from 'react';
import {
  subDays, endOfWeek, eachWeekOfInterval, eachDayOfInterval,
  format, isWithinInterval, startOfDay, endOfDay
} from 'date-fns';
import ChoreLogDetailsModal from './ChoreLogDetailsModal';
import HouseholdBanner from './HouseholdBanner';
import useAnalytics from '../hooks/useAnalytics';
import { ptsFor } from '../utils/points';
import RangePicker from './analytics/RangePicker';
import MetricChart from './analytics/MetricChart';
import Scoreboard from './analytics/Scoreboard';
import HistoryList from './analytics/HistoryList';
import { PAGE_SIZE } from './analytics/constants';

// ─── Component ────────────────────────────────────────────────────────────────
export default function Analytics({ currentUserId, household, roommates = [], onGoToChores }) {
  const [rangeDays, setRangeDays] = useState(30);
  const [metric, setMetric] = useState('points');
  const [page, setPage] = useState(0);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [scoreboardMode, setScoreboardMode] = useState('ranked'); // 'ranked' | 'leaders'
  const [selectedLog, setSelectedLog] = useState(null);
  const [historySearch, setHistorySearch] = useState('');
  const [sortField, setSortField] = useState('doneAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const { logs, loading, error, gradeLog, editLog, deleteLog, editGrade } =
    useAnalytics({ rangeDays, isCustomRange, startDate, endDate });

  useEffect(() => { setPage(0); }, [logs, metric]);

  const handleGradeSubmit = async (logId, gradeData) => {
    if (await gradeLog(logId, gradeData)) setSelectedLog(null);
  };

  const handleEditLog = async (logId, logData) => {
    if (await editLog(logId, logData)) setSelectedLog(null);
  };

  const handleDeleteLog = async (logId) => {
    if (await deleteLog(logId)) setSelectedLog(null);
  };

  const handleEditGrade = async (logId, gradeData) => {
    if (await editGrade(logId, gradeData)) setSelectedLog(null);
  };

  // ─── Derived data ─────────────────────────────────────────────────────────
  // All unique users who appear in the logs
  const allUsers = (() => {
    const map = new Map();
    logs.forEach(log => {
      log.user.forEach(u => { if (!map.has(u.id)) map.set(u.id, u); });
    });
    return Array.from(map.values());
  })();
  const allUserIds = allUsers.map(u => u.id);

  // Build weekly buckets for the chart
  const to = isCustomRange ? endOfDay(new Date(endDate)) : endOfDay(new Date());
  const from = isCustomRange ? startOfDay(new Date(startDate)) : startOfDay(subDays(new Date(), rangeDays));
  const isDaily = rangeDays <= 14;
  const intervals = isDaily
    ? eachDayOfInterval({ start: from, end: to })
    : eachWeekOfInterval({ start: from, end: to }, { weekStartsOn: 1 });

  const chartData = intervals.map(start => {
    const end = isDaily ? endOfDay(start) : endOfWeek(start, { weekStartsOn: 1 });
    const periodLogs = logs.filter(log =>
      isWithinInterval(new Date(log.doneAt), { start: start, end: end })
    );

    const entry = {
      label: format(start, isDaily ? 'EEE d' : 'MMM d'),
      fullRange: isDaily ? format(start, 'PPPP') : `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`
    };

    allUsers.forEach(user => {
      const userLogs = periodLogs.filter(log => log.user.some(u => u.id === user.id));

      if (metric === 'points') {
        entry[user.name] = userLogs.reduce((s, l) => s + ptsFor(l.actualMinutes, l.actualDifficulty), 0);
      } else if (metric === 'count') {
        entry[user.name] = userLogs.length;
      } else if (metric === 'minutes') {
        entry[user.name] = userLogs.reduce((s, l) => s + l.actualMinutes, 0);
      } else if (metric === 'difficulty') {
        entry[user.name] = userLogs.reduce((s, l) => s + l.actualDifficulty, 0);
      } else if (metric === 'grade') {
        const graded = userLogs.filter(l => l.othersUsersGrade.length > 0);
        if (graded.length === 0) {
          entry[user.name] = null;
        } else {
          const total = graded.reduce((s, l) => {
            const avg = l.othersUsersGrade.reduce((gs, g) => gs + g.grade, 0) / l.othersUsersGrade.length;
            return s + avg;
          }, 0);
          entry[user.name] = parseFloat((total / graded.length).toFixed(2));
        }
      }
    });

    return entry;
  });

  // User Aggregate Stats
  const userStats = allUsers.map(user => {
    const userLogs = logs.filter(log => log.user.some(u => u.id === user.id));
    const stats = {
      points: userLogs.reduce((s, l) => s + ptsFor(l.actualMinutes, l.actualDifficulty), 0),
      count: userLogs.length,
      minutes: userLogs.reduce((s, l) => s + l.actualMinutes, 0),
      difficulty: userLogs.reduce((s, l) => s + l.actualDifficulty, 0),
    };

    const graded = userLogs.filter(l => l.othersUsersGrade.length > 0);
    stats.grade = graded.length > 0
      ? parseFloat((graded.reduce((s, l) => s + (l.othersUsersGrade.reduce((gs, g) => gs + g.grade, 0) / l.othersUsersGrade.length), 0) / graded.length).toFixed(1))
      : 0;

    return { ...user, ...stats };
  });

  // Filter & Sort Logs
  const filteredSortedLogs = logs
    .filter(log => {
      const q = historySearch.toLowerCase();
      return (
        log.choreTitle.toLowerCase().includes(q) ||
        log.user.some(u => u.name.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      // String comparison for title
      if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  // Paginated history
  const totalPages = Math.ceil(filteredSortedLogs.length / PAGE_SIZE);
  const pageLogs = filteredSortedLogs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6 pb-6">
      {/* Header */}
      <HouseholdBanner
        household={household}
        height={104}
        eyebrow={household?.name || 'HOUSEHOLD'}
        heading="Analytics"
        memberColors={roommates.map(r => r.color).filter(Boolean)}
      />

      <div className="flex gap-2 items-center">
        <RangePicker
          isCustomRange={isCustomRange}
          setIsCustomRange={setIsCustomRange}
          rangeDays={rangeDays}
          setRangeDays={setRangeDays}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </div>

      <MetricChart
        metric={metric}
        setMetric={setMetric}
        loading={loading}
        error={error}
        allUsers={allUsers}
        allUserIds={allUserIds}
        chartData={chartData}
        onGoToChores={onGoToChores}
      />

      <Scoreboard
        scoreboardMode={scoreboardMode}
        setScoreboardMode={setScoreboardMode}
        loading={loading}
        allUsers={allUsers}
        allUserIds={allUserIds}
        userStats={userStats}
        metric={metric}
      />

      <HistoryList
        filteredSortedLogs={filteredSortedLogs}
        pageLogs={pageLogs}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        sortField={sortField}
        setSortField={setSortField}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        historySearch={historySearch}
        setHistorySearch={setHistorySearch}
        loading={loading}
        onSelectLog={setSelectedLog}
      />

      {/* Details Modal */}
      {selectedLog && (
        <ChoreLogDetailsModal
          log={selectedLog}
          currentUserId={currentUserId}
          onClose={() => setSelectedLog(null)}
          onGradeSubmit={handleGradeSubmit}
          onEditLog={handleEditLog}
          onDeleteLog={handleDeleteLog}
          onEditGrade={handleEditGrade}
        />
      )}
    </div>
  );
}
